import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, TablePagination, Box, Typography,
} from '@mui/material';
import { TAccountOverview } from '../../types/AccountOverview.type';
import './AccountsOverviewTable.scss';
import { useTranslation } from 'react-i18next';

interface IAccountsOverviewTableProps {
    accounts: TAccountOverview[];
    page: number;
    rowsPerPage: number;
    totalAccounts: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRowClick: (accountId: number) => void;
}

const AccountsOverviewTable: React.FC<IAccountsOverviewTableProps> = ({
    accounts,
    page,
    rowsPerPage,
    totalAccounts,
    onPageChange,
    onRowsPerPageChange,
    onRowClick,
}) => {
    const { t } = useTranslation("accountsOverview");

    const formatCurrency = (amount?: number | null): string => {
        const safe = amount ?? 0;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(safe);
    };

    const handleRowKeyDown =
        (accountId: number) => (e: React.KeyboardEvent<HTMLTableRowElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick(accountId);
            }
        };

    return (
        <Box className="accounts-overview-table">
            <TableContainer component={Paper} className="accounts-overview-table__container">
                <Table className="accounts-overview-table__table">
                    <TableHead>
                        <TableRow className="accounts-overview-table__header-row">
                            <TableCell className="accounts-overview-table__cell accounts-overview-table__header-cell">
                                {t("table.accountName")}
                            </TableCell>
                            <TableCell
                                align="center"
                                className="accounts-overview-table__cell accounts-overview-table__header-cell"
                            >
                                {t("table.casesCount")}
                            </TableCell>
                            <TableCell
                                align="right"
                                className="accounts-overview-table__cell accounts-overview-table__header-cell"
                            >
                                {t("table.totalReimbursed")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accounts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <Typography variant="body2">
                                        {t("empty")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (

                            accounts.map((account) => (
                                <TableRow
                                    key={account.id}
                                    className="accounts-overview-table__body-row"
                                    onClick={() => onRowClick(account.id)}
                                    onKeyDown={handleRowKeyDown(account.id)}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={t("table.aria.viewDetailsFor", { name: account.account_name })}
                                >
                                    <TableCell className="accounts-overview-table__cell accounts-overview-table__account-name">
                                        <Typography variant="body2" component="div" className="accounts-overview-table__account-name-text">
                                            {account.account_name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            className="accounts-overview-table__account-id"
                                        >
                                            {t("table.id")}: {account.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" className="accounts-overview-table__cell">
                                        <Typography variant="body2" className="accounts-overview-table__cases-count">
                                            {account.cases_count}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" className="accounts-overview-table__cell">
                                        <Typography variant="body2" className="accounts-overview-table__reimbursed-amount">
                                            {formatCurrency(account.total_reimbursed)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalAccounts}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                className="accounts-overview-table__pagination"
            />
        </Box>
    );
};

export default AccountsOverviewTable;